import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { formatSearchScore } from "~/hooks/search/adapters";
import { SearchType } from "~/hooks/search/types";

interface BookToExport {
  isbn: string;
  author: string;
  title: string;
  productForm: string;
  publishedBy: string;
  publishedOn: string;
  score?: number;
}

interface BooksExportContext {
  searchType: SearchType;
  searchQuery: string | undefined;
}

const buildBooksExportRows = (
  books: BookToExport[],
  includeMatch: boolean,
): { headers: string[]; rows: (string | number)[][] } => {
  const headers = [
    "ISBN",
    "Author",
    "Title",
    "Product form",
    "Published by",
    "Published on",
    ...(includeMatch ? ["Match"] : []),
  ];

  const rows = books.map((p) => {
    const base = [
      p.isbn,
      p.author,
      p.title,
      p.productForm,
      p.publishedBy,
      p.publishedOn,
    ];

    if (includeMatch) {
      base.push(formatSearchScore(p.score));
    }

    return base;
  });

  return { headers, rows };
};

export const saveBooksAsExcel = async (
  books: BookToExport[],
  context: BooksExportContext,
): Promise<void> => {
  const includeMatch = context.searchType === SearchType.SEMANTIC;
  const { headers, rows } = buildBooksExportRows(books, includeMatch);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("books");

  // Context rows
  worksheet.addRow(["Search Type", context.searchType]);
  worksheet.addRow(["Search Query", context.searchQuery ?? ""]);
  // Blank separator row for readability
  worksheet.addRow([""]);

  // Header row
  const headerRow = worksheet.addRow(headers);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
  });

  // Data rows
  rows.forEach((r) => worksheet.addRow(r));

  // Basic column sizing
  const widths = [18, 28, 40, 18, 28, 16, 12];
  headers.forEach((_, idx) => {
    const col = worksheet.getColumn(idx + 1);
    col.width = widths[idx] ?? 18;
    col.eachCell((cell) => {
      cell.alignment = { wrapText: true, vertical: "top" };
    });
  });

  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const buffer = await workbook.xlsx.writeBuffer();

  const today = new Date();
  const yyyyMmDd = today.toISOString().slice(0, 10);
  const filename = `reedy_books_export_${yyyyMmDd}.xlsx`;

  const blob = new Blob([buffer], { type: fileType });
  saveAs(blob, filename);
};
